import { useEffect, useState, useRef } from 'react';
import { useSubscription, gql } from 'urql';
import { useQueryClient } from '@tanstack/react-query';
const CROSS_VALIDATION_JOB_SUBSCRIPTION = gql `
  subscription CrossValidationJobUpdate($jobId: String!) {
    crossValidationJobUpdate(jobId: $jobId) {
      jobId
      shipmentId
      status
      progress
      error
      result
      timestamp
    }
  }
`;
const MERGE_JOB_SUBSCRIPTION = gql `
  subscription MergeJobUpdate($jobId: String!) {
    mergeJobUpdate(jobId: $jobId) {
      jobId
      shipmentId
      status
      progress
      error
      result
      timestamp
    }
  }
`;
export function useCrossValidationJobSubscription(jobId) {
    const [jobStatus, setJobStatus] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const hasConnectedOnce = useRef(false);
    const queryClient = useQueryClient();
    const [{ data, fetching: loading, error }] = useSubscription({
        query: CROSS_VALIDATION_JOB_SUBSCRIPTION,
        variables: { jobId: jobId || '' },
        pause: !jobId,
    });
    // Track connection status
    useEffect(() => {
        if (!jobId) {
            setConnectionStatus('disconnected');
            hasConnectedOnce.current = false;
            return;
        }
        if (error) {
            setConnectionStatus('disconnected');
            console.error('[CrossValidationJobSubscription] Subscription error:', error);
        }
        else if (!hasConnectedOnce.current) {
            const timer = setTimeout(() => {
                if (!error) {
                    setConnectionStatus('connected');
                    hasConnectedOnce.current = true;
                    console.log('[CrossValidationJobSubscription] Connected to subscription');
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
        else {
            setConnectionStatus('connected');
        }
    }, [error, jobId]);
    // Update job status when subscription data changes
    useEffect(() => {
        if (data?.crossValidationJobUpdate) {
            const update = data.crossValidationJobUpdate;
            setJobStatus(update);
            // Invalidate relevant queries when job completes
            if (update.status === 'completed' || update.status === 'failed') {
                queryClient.invalidateQueries({ queryKey: ['shipment', update.shipmentId] });
                queryClient.invalidateQueries({ queryKey: ['shipments', update.shipmentId, 'documents'] });
            }
        }
    }, [data, queryClient]);
    return {
        jobStatus,
        loading,
        error,
        connectionStatus,
        isConnected: connectionStatus === 'connected',
        isCompleted: jobStatus?.status === 'completed',
        isFailed: jobStatus?.status === 'failed',
        isProcessing: jobStatus?.status === 'processing',
    };
}
export function useMergeJobSubscription(jobId) {
    const [jobStatus, setJobStatus] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const hasConnectedOnce = useRef(false);
    const queryClient = useQueryClient();
    const [{ data, fetching: loading, error }] = useSubscription({
        query: MERGE_JOB_SUBSCRIPTION,
        variables: { jobId: jobId || '' },
        pause: !jobId,
    });
    // Track connection status
    useEffect(() => {
        if (!jobId) {
            setConnectionStatus('disconnected');
            hasConnectedOnce.current = false;
            return;
        }
        if (error) {
            setConnectionStatus('disconnected');
            console.error('[MergeJobSubscription] Subscription error:', error);
        }
        else if (!hasConnectedOnce.current) {
            const timer = setTimeout(() => {
                if (!error) {
                    setConnectionStatus('connected');
                    hasConnectedOnce.current = true;
                    console.log('[MergeJobSubscription] Connected to subscription');
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
        else {
            setConnectionStatus('connected');
        }
    }, [error, jobId]);
    // Update job status when subscription data changes
    useEffect(() => {
        if (data?.mergeJobUpdate) {
            const update = data.mergeJobUpdate;
            setJobStatus(update);
            // Invalidate relevant queries when job completes
            if (update.status === 'completed' || update.status === 'failed') {
                queryClient.invalidateQueries({ queryKey: ['shipment', update.shipmentId] });
                queryClient.invalidateQueries({ queryKey: ['shipments', update.shipmentId, 'documents'] });
            }
        }
    }, [data, queryClient]);
    return {
        jobStatus,
        loading,
        error,
        connectionStatus,
        isConnected: connectionStatus === 'connected',
        isCompleted: jobStatus?.status === 'completed',
        isFailed: jobStatus?.status === 'failed',
        isProcessing: jobStatus?.status === 'processing',
    };
}
